import { useCallback } from 'react';
import './style.scoped.css';
import TextInput from 'src/components/TextInput';
import PolicyCard from 'src/components/PolicyCard';
import Toolbar from 'src/components/Toolbar';
import Button from 'src/components/Button';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { confirmQuit, formatMoney, goBackCustom, paymentCheckDot, showError, validateTopUpFee } from 'src/utils';
import { createOrderTopUp } from 'src/api';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import LoadingService from 'src/utils/LoadingService';
import InfoIcon from 'src/assets/info_icon.svg';
import paymentSt, { PaymentStore } from 'src/stores/PaymentStore';
import { observer } from 'mobx-react-lite';
dayjs.extend(customParseFormat);

const CARD_DETAILS = ['payFrequency', 'periodicPremium', 'agentName', 'agentPhone', 'agentCode'];

interface TopUpPaymentObserverProps {
  paymentStore?: PaymentStore;
}
const TopUpPaymentObserver = observer(({ paymentStore } : TopUpPaymentObserverProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const _handleChange = useCallback((event) => {
    let value = event.target.value;
    value = value?.trim();
    if(value){
      let num = Number(value);
      if(!Number.isNaN(num) && !paymentCheckDot(value)){
        let min = Number(paymentStore?.topUpPaymentInfo?.topUpMinAmt);
        let max = Number(paymentStore?.topUpPaymentInfo?.topUpMaxAmt);
        let validate = validateTopUpFee(num, min, max);
        paymentStore.updateTopUpPaymentInfo({
          topUpValue: value,
          errTopUpValue: !validate ? 'error' : '',
          topUpValueInput: value,
          topUpValueFormat: formatMoney(value) + ' ' + paymentStore?.topUpPaymentInfo?.currency,
          amount: Number(value)
        });
      }
    }else{
      paymentStore.updateTopUpPaymentInfo({
        topUpValue: '',
        errTopUpValue: '',
        topUpValueInput: '',
        topUpValueFormat: '',
        amount: 0
      });
    }
  }, [paymentStore]);

  const _handleBlur = useCallback(() => {
    paymentStore.updateTopUpPaymentInfo({
      topUpValueInput: paymentStore?.topUpPaymentInfo?.topUpValueFormat,
    });
  }, [paymentStore]);

  const _handleFocus = useCallback(() => {
    paymentStore.updateTopUpPaymentInfo({
      topUpValueInput: paymentStore?.topUpPaymentInfo?.topUpValue,
    });
  }, [paymentStore]);

  const _handlePayment = async () => {
    let topUpAmount = paymentStore?.topUpPaymentInfo?.amount;
    if(paymentStore?.topUpPaymentInfo?.errTopUpValue) {
      showError(t('msg_09'));
    } else if(topUpAmount == 0){
      showError(t('msg_13'));
    }else{
      let request = {
        requestId: paymentStore?.topUpPaymentInfo?.requestId,
        amount: topUpAmount,
        policyNumber: paymentStore?.topUpPaymentInfo?.policyNum,
      }
      try{
        LoadingService.instance.current.open();
        let createOrderRes = await createOrderTopUp(request);
        LoadingService.instance.current.close();
        if (window?.ReactNativeWebView) {
          window?.ReactNativeWebView?.postMessage(
            JSON.stringify({
              type: 'PAYMENT_HUB_TRANSACTION',
              data: createOrderRes?.data,
            })
          );
        }
      }catch (error) {
        LoadingService.instance.current.close();
        showError(error?.response?.data?.error_message);
      }
    }
  };

  const _handleClose = () => {
    confirmQuit(history, InfoIcon);
  };

  const _handleBack = () => {
    goBackCustom(history);
  };

  return (
    <div className="screen-container bg-background view-top-up-payment">
      <Toolbar
        title={t('pay_fee_addition')}
        showClose={true}
        onClose={_handleClose}
        onBack={_handleBack}
      />
      <div className="max-w-md mx-auto screenScrollContainer">
        <div>
          <div className={"px-4 mt-4 pt-4 mb-4 bg-surface"}>
            <PolicyCard 
              policy={paymentStore?.topUpPaymentInfo ?? {}}
              showArrowIcon= {false}
              initShowDetail= {paymentStore?.topUpPaymentInfo?.showDetail ?? false}
              showBorder={paymentStore?.topUpPaymentInfo?.showDetail ?? false}
              details={CARD_DETAILS}
            />
          </div>
          <div className='bg-surface'>
            <div className="text-base font-bold text-onSurface px-4 pt-4">
              {t('additional_insurance_fee')}
            </div>
            <div className="px-4 pt-2 pb-4 bodyText2 text-onSurfaceVariant border border-line">{t('hint_additional_insurance_fee')}</div>
          </div>

          <div className='bg-surface mb-8'>
            {/* <Checkbox
              textComponent={
                <div>
                  <div className="text-base font-bold text-onSurface">
                  {t('additional_insurance_fee')}
                  </div>
                  <div className="bodyText2 text-onSurfaceVariant">{t('hint_additional_insurance_fee')}</div>
                </div>
              }
              checked={true}
              onCheck={() => {}}
              changeBackground={false}
              type="card"
            /> */}
            <div className="px-4 pb-6 pt-5">
              <TextInput
                label={t('topup_fee_hint')}
                placeholder={''}
                value={paymentStore?.topUpPaymentInfo?.topUpValueInput}
                onChange={_handleChange}
                onBlur={_handleBlur}
                onFocus={_handleFocus}
                type={'text'}
                helperText={
                  paymentStore?.topUpPaymentInfo?.disabledInput
                  ? `${t('top_up_input_disabled_hint')}`
                  : `${t('min')} ${formatMoney(paymentStore?.topUpPaymentInfo?.topUpMinAmt)} ${paymentStore?.topUpPaymentInfo?.currency}; ${t('max')} ${formatMoney(paymentStore?.topUpPaymentInfo?.topUpMaxAmt)} ${paymentStore?.topUpPaymentInfo?.currency}`
                }
                disabled={paymentStore?.topUpPaymentInfo?.disabledInput}
              />
              {!!paymentStore?.topUpPaymentInfo?.errTopUpValue && (
                <div className={'helperTextError'}>{t('msg_09')}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="bottomButtonBlock">
        <div className='border border-line'>
          <div className="flex flex-row pt-1 pl-4 pr-4 pb-4 bg-surface">
            <div className="flex-1 smallTitle2">{t('total_fee')}</div>
            <div className="blockTitlePrimary">
              {formatMoney(paymentStore?.topUpPaymentInfo?.amount) + ` ${paymentStore?.topUpPaymentInfo?.currency ?? ""}`}
            </div>
        </div>
        </div>
        <div className='px-4 pt-3'>
          <Button
            text={t('payment')}
            onClick={_handlePayment}
            extraClassName={'w-full'}
            disabled={Number(paymentStore?.topUpPaymentInfo?.amount) == 0}
          />
        </div>
      </div>
    </div>
  );
});

const TopUpPayment = () => <TopUpPaymentObserver paymentStore={paymentSt} />;
export default TopUpPayment;
